import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import CategoryNav from '@/components/feature/CategoryNav';
import { categories, getDramasByCategory } from '@/mocks/dramas';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = useMemo(() => decodeURIComponent(slug || ''), [slug]);
  const dramaList = useMemo(() => getDramasByCategory(category), [category]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const isValidCategory = (categories as readonly string[]).includes(category);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <nav className="max-w-7xl mx-auto px-4 md:px-6 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">分类浏览</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-800 font-medium">{category}</li>
          </ol>
        </nav>

        {/* Category Header */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="flex items-center gap-3 mb-2">
            <i className="ri-movie-2-line text-primary-500 text-2xl"></i>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              {category}
            </h1>
          </div>
          <p className="text-foreground-500 text-sm md:text-base">
            {isValidCategory
              ? `被窝影院 ${category} 频道 - 高清流畅在线看，全集免费观看，每日更新热门${category}。`
              : '未找到该分类，请浏览以下可选分类。'}
          </p>
        </section>

        {/* Category Nav */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-6 border-b border-background-200/50" aria-label="分类切换">
          <CategoryNav active={isValidCategory ? category : undefined} />
        </section>

        {/* Drama Grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" aria-label={`${category}列表`}>
          {isValidCategory && dramaList.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-foreground-500 text-sm">
                  共找到 <strong className="text-foreground-800">{dramaList.length}</strong> 部{category}
                </p>
                <div className="flex items-center gap-2 text-sm text-foreground-500">
                  <span>排序：</span>
                  <select className="bg-background-100 border border-background-200/50 rounded-md px-2 py-1 text-sm text-foreground-700 cursor-pointer" aria-label="排序方式">
                    <option value="rating">评分最高</option>
                    <option value="newest">最新上线</option>
                    <option value="popular">最受欢迎</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {dramaList.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto rounded-full bg-background-100 border border-background-200/50 flex items-center justify-center mb-4">
                <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
              </div>
              <p className="text-foreground-500 text-base">该分类暂无内容</p>
              <p className="text-foreground-400 text-sm mt-1">请尝试浏览其他分类</p>
              <div className="mt-6">
                <Link
                  to="/"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-4-line"></i> 返回首页
                </Link>
              </div>
            </div>
          )}
        </section>

        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `被窝影院 ${category} - 在线免费观看`,
            "description": `被窝影院${category}频道，高清流畅在线看${category}，全集免费观看，每日更新。`,
            "url": `https://www.beiwoyingyuan.com/category/${encodeURIComponent(category)}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "被窝影院",
              "url": "https://www.beiwoyingyuan.com/"
            }
          })
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.beiwoyingyuan.com/" },
              { "@type": "ListItem", "position": 2, "name": "分类浏览", "item": "https://www.beiwoyingyuan.com/" },
              { "@type": "ListItem", "position": 3, "name": category, "item": `https://www.beiwoyingyuan.com/category/${encodeURIComponent(category)}` }
            ]
          })
        }} />
      </main>

      <Footer />
    </div>
  );
}